import React, { useState } from "react";
import axios from "axios";
import styled from "styled-components";
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaUser,
  FaPaperPlane,
} from "react-icons/fa";

const ContactContainer = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 50px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;

  @media screen and (max-width: 768px) {
    padding: 35px 0;
  }
`;

const ContactWrapper = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  align-items: stretch;
  grid-gap: 25px;
  padding: 0 35px;

  @media screen and (max-width: 968px) {
    grid-template-columns: 1fr;
    padding: 0 25px;
  }

  @media screen and (max-width: 480px) {
    padding: 0 20px;
  }
`;

const ContactH1 = styled.h1`
  font-size: 2rem;
  color: #010606;
  margin-bottom: 10px;
  font-weight: 700;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const ContactSubtitle = styled.p`
  font-size: 0.95rem;
  color: #5a5a5a;
  text-align: center;
  margin-bottom: 28px;
  max-width: 550px;

  @media screen and (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 25px;
  }
`;

const ContactInfoCard = styled.div`
  background: #ffffff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media screen and (max-width: 968px) {
    padding: 22px;
  }

  @media screen and (max-width: 480px) {
    padding: 18px;
  }
`;

const ContactInfoTitle = styled.h2`
  font-size: 1.4rem;
  margin-bottom: 5px;
  color: #1976d3;
  font-weight: 600;

  @media screen and (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const ContactInfoText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #5a5a5a;
  margin-bottom: 8px;
`;

const ContactDetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #1976d3;
    transform: translateX(8px);
    box-shadow: 0 4px 12px rgba(25, 118, 211, 0.3);

    p,
    a {
      color: #ffffff;
    }

    .icon-wrapper {
      background: #ffffff;
      color: #1976d3;
    }
  }

  @media screen and (max-width: 480px) {
    padding: 10px;
    gap: 10px;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  background: #1976d3;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1rem;
  transition: all 0.3s ease;

  @media screen and (max-width: 480px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    font-size: 0.9rem;
  }
`;

const ContactDetailText = styled.div`
  flex: 1;
`;

const ContactDetailLabel = styled.p`
  font-size: 0.75rem;
  color: #888;
  margin-bottom: 2px;
  font-weight: 500;
  transition: color 0.3s ease;
`;

const ContactDetailValue = styled.a`
  font-size: 0.9rem;
  color: #010606;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.3s ease;
  word-break: break-word;

  @media screen and (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const ContactFormCard = styled.form`
  background: #ffffff;
  padding: 25px;
  border-radius: 12px;
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media screen and (max-width: 968px) {
    padding: 22px;
  }

  @media screen and (max-width: 480px) {
    padding: 18px;
    gap: 10px;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.35rem;
  color: #010606;
  margin-bottom: 3px;
  font-weight: 600;

  @media screen and (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 10px 14px;
  font-size: 0.9rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f9fa;
  color: #000000;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    background: #ffffff;
    border-color: #1976d3;
    box-shadow: 0 0 0 2px rgba(25, 118, 211, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f0f0f0;
    cursor: not-allowed;
  }

  @media screen and (max-width: 480px) {
    padding: 9px 12px;
    font-size: 0.88rem;
  }
`;

const TextArea = styled.textarea`
  padding: 10px 14px;
  font-size: 0.9rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f9fa;
  color: #000000;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    background: #ffffff;
    border-color: #1976d3;
    box-shadow: 0 0 0 2px rgba(25, 118, 211, 0.1);
  }

  &::placeholder {
    color: #999;
  }

  &:disabled {
    background: #f0f0f0;
    cursor: not-allowed;
  }

  @media screen and (max-width: 480px) {
    padding: 9px 12px;
    font-size: 0.88rem;
    min-height: 90px;
  }
`;

const SubmitButton = styled.button`
  padding: 11px 30px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #1976d3 0%, #1565c0 100%);
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(25, 118, 211, 0.3);
  margin-top: 3px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(25, 118, 211, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media screen and (max-width: 480px) {
    padding: 10px 25px;
    font-size: 0.9rem;
  }
`;

const SuccessMessage = styled.div`
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  color: #fff;
  padding: 10px 14px;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
  font-size: 0.85rem;
  box-shadow: 0 3px 10px rgba(76, 175, 80, 0.3);
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
  color: #fff;
  padding: 10px 14px;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
  font-size: 0.85rem;
  box-shadow: 0 3px 10px rgba(244, 67, 54, 0.3);
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess(false);

    try {
      await axios.post(`${baseUrl}/auth/contact`, formData);
      setSuccess(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      setError(
        error.response?.data?.error ||
        "Failed to send message. Please try again.",
      );

      // Hide error message after 5 seconds
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ContactContainer id="contact">
      <ContactH1>Get In Touch</ContactH1>
      <ContactSubtitle>
        Have a question or want to work together? We'd love to hear from you.
      </ContactSubtitle>
      <ContactWrapper>
        <ContactInfoCard>
          <div>
            <ContactInfoTitle>Let's Talk</ContactInfoTitle>
            <ContactInfoText>
              We're here to help and answer any question you might have. We look
              forward to hearing from you!
            </ContactInfoText>
          </div>

          <ContactDetailItem>
            <IconWrapper className="icon-wrapper">
              <FaEnvelope />
            </IconWrapper>
            <ContactDetailText>
              <ContactDetailLabel>Email</ContactDetailLabel>
              <ContactDetailValue href="mailto:lmtss1695@gmail.com">
                lmtss1695@gmail.com
              </ContactDetailValue>
            </ContactDetailText>
          </ContactDetailItem>

          <ContactDetailItem>
            <IconWrapper className="icon-wrapper">
              <FaPhone />
            </IconWrapper>
            <ContactDetailText>
              <ContactDetailLabel>Phone</ContactDetailLabel>
              <ContactDetailValue href="tel:+231776285454">
                +231 77 628 5454
              </ContactDetailValue>
            </ContactDetailText>
          </ContactDetailItem>

          <ContactDetailItem>
            <IconWrapper className="icon-wrapper">
              <FaMapMarkerAlt />
            </IconWrapper>
            <ContactDetailText>
              <ContactDetailLabel>Address</ContactDetailLabel>
              <ContactDetailValue as="p">
                Caldwell Bridge, Montserrado, Liberia
              </ContactDetailValue>
            </ContactDetailText>
          </ContactDetailItem>
        </ContactInfoCard>

        <ContactFormCard onSubmit={handleSubmit}>
          <FormTitle>Send us a Message</FormTitle>

          {success && (
            <SuccessMessage>
              Thank you for your message! We'll get back to you soon.
            </SuccessMessage>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InputGroup>
            <Label htmlFor="name">Full Name *</Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="john@example.com"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              placeholder="+1 (555) 123-4567"
              value={formData.phone}
              onChange={handleChange}
              disabled={submitting}
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="subject">Subject *</Label>
            <Input
              type="text"
              id="subject"
              name="subject"
              placeholder="How can we help you?"
              value={formData.subject}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="message">Message *</Label>
            <TextArea
              id="message"
              name="message"
              placeholder="Tell us more about your inquiry..."
              value={formData.message}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </InputGroup>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? (
              "Sending..."
            ) : (
              <>
                <FaPaperPlane />
                Send Message
              </>
            )}
          </SubmitButton>
        </ContactFormCard>
      </ContactWrapper>
    </ContactContainer>
  );
};

export default Contact;
